import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_KEY = "crickpad-token";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = checking; null = anonymous; object = logged in
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const persistToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setToken(t);
  }, []);

  const fetchMe = useCallback(async (t) => {
    if (!t) {
      setUser(null);
      return;
    }
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setUser(data);
    } catch {
      persistToken(null);
      setUser(null);
    }
  }, [persistToken]);

  const refreshMe = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    await fetchMe(t);
  }, [fetchMe]);

  useEffect(() => {
    fetchMe(token);
  }, [token, fetchMe]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    persistToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, role, inviteCode) => {
    const payload = { email, password, name, role };
    if (inviteCode) payload.invite_code = inviteCode;
    const { data } = await axios.post(`${API}/auth/register`, payload);
    persistToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    persistToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}

export function getStoredToken() {
  return localStorage.getItem(TOKEN_KEY);
}

export function clearStoredToken() {
  localStorage.removeItem(TOKEN_KEY);
}
